import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { ORAOutboundDialogData, ORAOutboundResponse } from '../../../models/oraoutbound.model';
import { OraoutboundService } from '../../../services/oraoutbound.service';
import { IdName } from '../../../interfaces/pricematrixmodel';
import { numericInputLimit } from '../../shared/commonutils/helper';

@Component({
  selector: 'app-add-edit-oraoutbound',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatDialogModule
  ],
  templateUrl: './add-edit-oraoutbound.component.html',
  styleUrl: './add-edit-oraoutbound.component.scss'
})
export class AddEditOraoutboundComponent implements OnInit {

private dialogRef = inject(MatDialogRef<AddEditOraoutboundComponent>);
  private data: ORAOutboundDialogData = inject(MAT_DIALOG_DATA);
  private lookup: OraoutboundService = inject(OraoutboundService);
  private fb = inject(FormBuilder);

  // Component State
  oraForm!: FormGroup;
  revenueTypes = signal<IdName[]>([]);
  canSave: boolean = false;
  isEditMode = signal(false);
  dialogTitle = signal('');

  constructor() {
    console.log("AddEditORAOutbound Dialog data:", this.data);
    this.canSave = this.data.canSave;
  }
  
  ngOnInit(): void {
    this.loadDropdowns();
    const initialData: Partial<ORAOutboundResponse> = this.data.rowData || {};
    this.isEditMode.set(this.data.taskType === 'Edit');    
    const displayDate = initialData.captureDate ? new Date(initialData.captureDate) : new Date();

    this.dialogTitle.set(
      `${this.isEditMode() ? 'Edit' : 'Add'} ORA Outbound on ${displayDate.toLocaleDateString()}`
    );
    
    this.oraForm = this.fb.group({
      revenueType: [initialData.revenueType || '', [Validators.required]],
      captureDate: [displayDate, [Validators.required]],
      numberofHauls: [initialData.numberofHauls, [Validators.required, Validators.min(0)]],
      totalTons: [initialData.totalTons, [Validators.required, Validators.min(0)]]
    });
  }

  loadDropdowns(): void {
   this.lookup.getRecyclingTypes().subscribe({
      next: (types) => {
        this.revenueTypes.set(types);
        if (!this.isEditMode() && types.length > 0) {
          this.oraForm.get('revenueType')?.patchValue(types[0].id);
        }
      }
    });  
  }

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  }

  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

  onCancel(){
    this.dialogRef.close(false);
  }

  onSave(){
    if(this.oraForm.valid){
      const formValue = this.oraForm.value;
      const payload: ORAOutboundResponse = {
        id: this.isEditMode() && this.data.rowData ? this.data.rowData.id : 0,
        revenueType: formValue.revenueType,
        captureDate: formValue.captureDate,
        numberofHauls: formValue.numberofHauls,
        totalTons: formValue.totalTons,
        revenueTypeName: this.revenueTypes().find(rt => rt.id === formValue.revenueType)?.name || ''
      };
      this.lookup.saveOrAOutbound(payload).subscribe({
        next: (res) => {
          this.dialogRef.close(true);
        }
      });
    }else{
      this.oraForm.markAllAsTouched();
    }
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    input.value = val;

    // 6. Parsing for the backing variables    
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.oraForm.get(controlName)?.setValue(null, { emitEvent: true });
    } else if (val.endsWith('.')) {
      this.oraForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.oraForm.get(controlName)?.setValue(parsedValue, { emitEvent: true });
    }

    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;   // put "14.10" back so next keystroke appends correctly
      }
    });
    this.oraForm.markAsDirty();
    this.oraForm.updateValueAndValidity();
  }
}
